import {Component, DestroyRef, inject, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {Land} from '../../models/land/Land';
import {PFLICHTFELDMARKER} from '../../../assets/constants/constants';
import {StudentTicketMandateHolderDto} from '../../models/student-ticket/payment/StudentTicketMandateHolderDto';
import {ValidatorService} from 'angular-iban';
import {InputComponent} from '../form/input/input.component';
import {TranslatePipe} from '@ngx-translate/core';
import {SelectComponent} from '../form/select/select.component';
import {PlzValidationHelperService} from '../../@core/utils/forms/plz-validation-helper.service';
import {CheckboxComponent} from '../form/checkbox/checkbox.component';

/**
 * Wiederverwendbare Mandatsträger-Formularlogik - eingebettet sowohl im Verifizierungs-Wizard
 * (PaymentStepComponent) als auch im MandateHolderModalComponent.
 */
@Component({
  selector: 'app-mandate-holder-form',
  standalone: true,
  imports: [InputComponent, TranslatePipe, ReactiveFormsModule, SelectComponent, CheckboxComponent],
  templateUrl: './mandate-holder-form.component.html',
})
export class MandateHolderFormComponent implements OnInit, OnChanges {

  @Input() initialData: StudentTicketMandateHolderDto | null;
  // Erlaubt unterschiedliche data-cy-Präfixe je nach Einbettungskontext (Wizard-Step vs. Modal)
  @Input() testIdPrefix = '';

  @ViewChildren(InputComponent) private inputComponents: QueryList<InputComponent>;
  @ViewChildren(SelectComponent) private selectComponents: QueryList<SelectComponent>;
  @ViewChildren(CheckboxComponent) private checkboxComponents: QueryList<CheckboxComponent>;

  form: UntypedFormGroup | null = null;
  protected countryList = [];
  protected formReady = false;
  protected bicRequiredMarker = '';

  private readonly fb = inject(UntypedFormBuilder);
  protected readonly plzHelper = inject(PlzValidationHelperService);

  ngOnInit(): void {
    this.initCountryList();
    this.initForm();
    this.form?.get('mandateHolderIban')?.valueChanges.subscribe((iban: string) => {
      this.markBicRequired(iban);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && !changes['initialData'].firstChange) {
      this.initializeValues();
    }
  }

  initCountryList(): void {
    this.countryList = Object.keys(Land).map((name) => {
      return {name, value: Land[name as keyof typeof Land]};
    });
  }

  initForm(): void {
    this.form = this.fb.group({
      mandateHolderFirstname: [null, Validators.required],
      mandateHolderLastname: [undefined, Validators.required],
      mandateHolderDateOfBirth: [undefined, Validators.required],
      mandateHolderStreet: ['', Validators.required],
      mandateHolderAddressAddition: [undefined],
      mandateHolderPostalCode: [undefined, Validators.required],
      mandateHolderCity: [undefined, Validators.required],
      mandateHolderCountry: [undefined, Validators.required],
      mandateHolderIban: [undefined, [Validators.required, this.validateIbanWhenDirty]],
      mandateHolderBankName: [undefined, Validators.required],
      mandateHolderBic: [undefined],
      mandateHolderSepaMandateAccepted: [false, Validators.requiredTrue]
    });
    this.initializeValues();
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private validateIbanWhenDirty(control: AbstractControl): ValidationErrors | null {
    return control.dirty ? ValidatorService.validateIban(control) : null;
  }

  initializeValues(): void {
    if (!this.form) return;
    this.form.get('mandateHolderFirstname').patchValue(this.initialData?.mandateHolderFirstname);
    this.form.get('mandateHolderLastname').patchValue(this.initialData?.mandateHolderLastname);
    this.form.get('mandateHolderDateOfBirth').patchValue(this.initialData?.mandateHolderDateOfBirth);
    this.form.get('mandateHolderStreet').patchValue(this.initialData?.mandateHolderStreet);
    this.form.get('mandateHolderAddressAddition').patchValue(this.initialData?.mandateHolderAddressAddition);
    this.form.get('mandateHolderPostalCode').patchValue(this.initialData?.mandateHolderPostalCode);
    this.form.get('mandateHolderCity').patchValue(this.initialData?.mandateHolderCity);
    this.form.get('mandateHolderBankName').patchValue(this.initialData?.mandateHolderBankName);
    this.form.get('mandateHolderIban').patchValue(this.initialData?.mandateHolderIban);
    this.form.get('mandateHolderBic').patchValue(this.initialData?.mandateHolderBic);
    this.form.get('mandateHolderSepaMandateAccepted').patchValue(this.initialData?.mandateHolderSepaMandateAccepted ?? false);
    const country = this.countryList.find(c => c.name === this.initialData?.mandateHolderCountry);
    this.form.get('mandateHolderCountry').patchValue(country?.name);
    this.formReady = true;
  }

  markBicRequired(ibanString: string): void {
    if (!ibanString ||
      (ibanString?.length === 1 && ibanString?.toUpperCase().startsWith('D')) ||
      (ibanString?.length >= 2 && ibanString?.toUpperCase().startsWith('DE'))) {
      this.form.get('mandateHolderBic')?.removeValidators([Validators.required]);
      this.form.get('mandateHolderBic')?.updateValueAndValidity();
      this.bicRequiredMarker = '';
    } else {
      this.form.get('mandateHolderBic')?.addValidators([Validators.required]);
      this.form.get('mandateHolderBic')?.updateValueAndValidity();
      this.bicRequiredMarker = PFLICHTFELDMARKER;
    }
  }

  setPlzPatternValidator(plzControlName: string, landControlName: string): void {
    const land = this.getFormItem(landControlName)?.value;
    if (land) {
      this.getFormItem(plzControlName)?.setValidators([
        Validators.maxLength(this.plzHelper.getMaxPlzLength(land)),
        Validators.pattern(this.plzHelper.getPlzPattern(land))
      ]);
    } else {
      this.getFormItem(plzControlName)?.clearValidators();
    }
    this.getFormItem(plzControlName)?.updateValueAndValidity();
  }

  getMaxPlzLength(landControlName: string): number {
    return this.plzHelper.getMaxPlzLength(this.getFormItem(landControlName)?.value);
  }

  getPlzPattern(landControlName: string): string {
    return this.plzHelper.getPlzPattern(this.getFormItem(landControlName)?.value);
  }

  getFormItem(s: string) {
    return this.form?.get(s);
  }

  isValid(): boolean {
    return !!this.form?.valid;
  }

  getFormValue(): StudentTicketMandateHolderDto {
    const formValue = this.form.value;
    return {
      mandateHolderFirstname: formValue.mandateHolderFirstname,
      mandateHolderLastname: formValue.mandateHolderLastname,
      mandateHolderDateOfBirth: formValue.mandateHolderDateOfBirth,
      mandateHolderStreet: formValue.mandateHolderStreet,
      mandateHolderAddressAddition: formValue.mandateHolderAddressAddition,
      mandateHolderPostalCode: formValue.mandateHolderPostalCode,
      mandateHolderCity: formValue.mandateHolderCity,
      mandateHolderCountry: formValue.mandateHolderCountry,
      mandateHolderBankName: formValue.mandateHolderBankName,
      mandateHolderIban: formValue.mandateHolderIban,
      mandateHolderBic: formValue.mandateHolderBic,
      mandateHolderSepaMandateAccepted: formValue.mandateHolderSepaMandateAccepted
    };
  }

  /** Markiert alle Felder als "touched", damit Validierungsfehler im UI sichtbar werden. */
  markAllAsTouched(): void {
    this.form?.markAllAsTouched();
  }

  /** Liefert eine kommagetrennte Liste der Labels aktuell ungültiger Felder (für Fehlermeldungen im Modal). */
  getInvalidFieldLabels(): string {
    let notValid = '';
    const controls = this.form.controls;
    this.inputComponents.forEach(component => {
      const controlName = component.id;
      if (controls[controlName] && !controls[controlName].valid) {
        notValid += component.getTitle().replace(PFLICHTFELDMARKER, '') + ', ';
      }
    });
    this.selectComponents.forEach(component => {
      const controlName = component.id;
      if (controls[controlName] && !controls[controlName].valid) {
        notValid += component.getTitle().replace(PFLICHTFELDMARKER, '') + ', ';
      }
    });
    this.checkboxComponents.forEach(component => {
      const controlName = component.id;
      if (controls[controlName] && !controls[controlName].valid) {
        notValid += component.getTitle().replace(PFLICHTFELDMARKER, '') + ', ';
      }
    });
    return notValid.substring(0, notValid.length - 2);
  }

  protected readonly PFLICHTFELDMARKER = PFLICHTFELDMARKER;
}
